import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  Stack,
  IconButton,
  Tooltip,
  FormGroup,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Pencil,
  Trash,
  UserPlus,
  PaperPlane,
  Copy,
  Info,
} from '@phosphor-icons/react';
import api from '../../utils/api';
import { useBusiness } from '../../context/BusinessContext';
import { getAvatarSrc } from '../../utils/avatarConstants';
import { formatGBPFromPounds } from '../../utils/currency';
import { getPriceSuffix } from '../../utils/priceDisplay';
import useThemeStyles from '../../theme/useThemeStyles';

function People() {
  const ALL_PERMISSION_KEYS = [
    'view_bookings', 'manage_bookings', 'view_availability', 'manage_availability',
    'view_profile', 'edit_profile', 'manage_staff', 'view_analytics',
    'manage_policies', 'view_messages', 'send_messages',
    'view_financials', 'manage_financials', 'manage_services'
  ];
  const PERMISSION_DETAILS = {
    view_bookings: {
      label: 'View Couples & Bookings',
      description: 'See couples, bookings, form submissions, agreements, and products.'
    },
    manage_bookings: {
      label: 'Manage Bookings & Forms',
      description: 'Create and update bookings, and use the form builder.'
    },
    view_availability: {
      label: 'View Schedules',
      description: 'See team schedules and availability.'
    },
    manage_availability: {
      label: 'Manage Schedules',
      description: 'Create and edit staff schedules, shifts, and availability rules.'
    },
    view_profile: {
      label: 'View Profile',
      description: 'View the business profile and business settings.'
    },
    edit_profile: {
      label: 'Edit Profile',
      description: 'Update profile details, branding, and business information.'
    },
    manage_staff: {
      label: 'Manage People',
      description: 'Invite, edit, and remove team members in People.'
    },
    view_analytics: {
      label: 'View Analytics',
      description: 'See performance and booking analytics.'
    },
    manage_policies: {
      label: 'Manage Policies',
      description: 'Edit policy and terms settings.'
    },
    view_messages: {
      label: 'View Messages',
      description: 'Open and read message conversations.'
    },
    send_messages: {
      label: 'Send Messages',
      description: 'Reply to couples and send new messages.'
    },
    view_financials: {
      label: 'View Invoices & Billing',
      description: 'View invoices, billing, and subscription details.'
    },
    manage_financials: {
      label: 'Manage Financials',
      description: 'Make billing and subscription changes.'
    },
    manage_services: {
      label: 'Manage Appointments',
      description: 'Configure appointment/service setup, including service types and booking options.'
    }
  };

  const styles = useThemeStyles();
  const { selectedBusiness, hasPermission } = useBusiness();
  const businessId = selectedBusiness?.business?._id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [members, setMembers] = useState([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    role: 'staff',
    permissions: []
  });
  const [editFormData, setEditFormData] = useState({
    role: 'staff',
    permissions: []
  });
  const [editingMember, setEditingMember] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [removingMember, setRemovingMember] = useState(false);
  const [showInviteLinkDialog, setShowInviteLinkDialog] = useState(false);
  const [selectedInviteLink, setSelectedInviteLink] = useState('');
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [transferToMemberId, setTransferToMemberId] = useState('');
  const [paymentCheckDialogOpen, setPaymentCheckDialogOpen] = useState(false);
  const [paymentRequired, _setPaymentRequired] = useState(null);
  const [pendingMemberInvite, _setPendingMemberInvite] = useState(null);
  const [addSeatDialogOpen, setAddSeatDialogOpen] = useState(false);
  const [pendingSeatAddition, setPendingSeatAddition] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [additionalUserConfirmOpen, setAdditionalUserConfirmOpen] = useState(false);
  const [pendingInvite, setPendingInvite] = useState(null);

  useEffect(() => {
    if (businessId) {
      fetchMembers();
      fetchSubscriptionDetails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId]);

  // Handle return from Stripe checkout for seat addition
  useEffect(() => {
    if (!businessId) return; // Wait for businessId to be set
    
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('seat_added') === 'true') {
      const _memberId = urlParams.get('member_id');
      setSuccess('Payment successful! Team member has been added to your subscription.');
      fetchMembers();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (urlParams.get('seat_canceled') === 'true') {
      setError('Payment was cancelled. Team member was not added.');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId]);

  const fetchMembers = async () => {
    if (!businessId) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get(`/api/business-members/business/${businessId}`);
      setMembers(response.data.members || []);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  };


  const fetchSubscriptionDetails = async () => {
    if (!businessId) return;
    
    try {
      const response = await api.get(`/api/business/${businessId}/subscriptions`);
      setSubscriptionDetails(response.data);
    } catch (error) {
      setSubscriptionDetails(null);
    }
  };

  // Calculate user limits based on current plan
  const getUserLimitInfo = () => {
    if (!subscriptionDetails) {
      return { current: 0, max: null, planName: 'Free' };
    }

    const planKey = subscriptionDetails.currentPlanMembershipLevelKey || 'supplier_free';
    const currentUsers = members.filter(m => m.status === 'active').length;
    
    // Get plan info
    let maxUsers = null;
    let planName = 'Free';
    
    if (planKey === 'supplier_individual') {
      maxUsers = 1;
      planName = 'Individual';
    } else if (planKey === 'supplier_team') {
      const baseUsers = 3;
      const additionalUsers = subscriptionDetails.additionalUsers?.count || 0;
      maxUsers = baseUsers + additionalUsers;
      planName = 'Team';
    } else if (planKey === 'supplier_enterprise') {
      maxUsers = null; // Unlimited
      planName = 'Enterprise';
    }

    return { current: currentUsers, max: maxUsers, planName, planKey };
  };

  const handleInviteClick = () => {
    const limitInfo = getUserLimitInfo();
    const planKey = limitInfo.planKey;

    // Individual plan: Show upgrade prompt
    if (planKey === 'supplier_individual') {
      setError('Individual plan allows only 1 user. Upgrade to Team plan to invite team members.');
      // Could navigate to billing page here
      return;
    }

    // Team plan: Check if adding beyond base (3 users)
    if (planKey === 'supplier_team') {
      const baseUsers = 3;
      if (limitInfo.current >= baseUsers) {
        // Adding 4th+ user - show confirmation
        setPendingInvite({
          willBeUserNumber: limitInfo.current + 1,
          additionalUserPrice: 39.99
        });
        setAdditionalUserConfirmOpen(true);
        return;
      }
    }

    // Proceed with normal invite flow
    setInviteFormData({
      email: '',
      role: 'staff',
      permissions: getDefaultPermissions('staff')
    });
    setInviteDialogOpen(true);
  };


  const getDefaultPermissions = (role) => {
    switch (role) {
      case 'owner':
        return [...ALL_PERMISSION_KEYS];
      case 'admin':
        return [...ALL_PERMISSION_KEYS];
      case 'manager':
        return [
          'view_bookings', 'manage_bookings', 'view_availability', 'manage_availability',
          'view_profile', 'edit_profile', 'view_analytics',
          'view_messages', 'send_messages', 'manage_services'
        ];
      case 'staff':
        return [
          'view_bookings', 'view_profile',
          'view_messages', 'send_messages'
        ];
      default:
        return [];
    }
  };

  const handleConfirmAdditionalUser = async () => {
    if (!pendingInvite) return;
    
    try {
      setInviting(true);
      setError('');
      
      // Send invitation with confirmation
      const response = await api.post(`/api/business/${businessId}/members`, {
        email: pendingInvite.email,
        role: pendingInvite.role,
        permissions: pendingInvite.permissions,
        confirmAdditionalUser: true // Flag to proceed with additional user charge
      });

      setSuccess(`Invitation sent successfully to ${pendingInvite.email}. Additional user charge will be added to your subscription.`);
      setAdditionalUserConfirmOpen(false);
      setPendingInvite(null);
      fetchMembers();
      fetchSubscriptionDetails();
      
      // If invite link is returned, show it
      if (response.data.inviteLink) {
        setSelectedInviteLink(response.data.inviteLink);
        setShowInviteLinkDialog(true);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleInviteRoleChange = (role) => {
    const defaultPerms = getDefaultPermissions(role);
    setInviteFormData({
      ...inviteFormData,
      role,
      permissions: defaultPerms
    });
  };

  const handlePermissionToggle = (permission) => {
    const currentPerms = inviteFormData.permissions || [];
    const newPerms = currentPerms.includes(permission)
      ? currentPerms.filter(p => p !== permission)
      : [...currentPerms, permission];
    setInviteFormData({
      ...inviteFormData,
      permissions: newPerms
    });
  };

  const handleSendInvite = async () => {
    if (!inviteFormData.email || !inviteFormData.role) {
      setError('Email and role are required');
      return;
    }

    try {
      setInviting(true);
      setError('');

      const response = await api.post(`/api/business/${businessId}/members`, {
        email: inviteFormData.email,
        role: inviteFormData.role,
        permissions: inviteFormData.permissions
      });

      // Check if confirmation is required for additional user
      if (response.data.requiresConfirmation) {
        setInviteDialogOpen(false);
        setPendingInvite({
          email: inviteFormData.email,
          role: inviteFormData.role,
          permissions: inviteFormData.permissions,
          willBeUserNumber: response.data.willBeUserNumber,
          additionalUserPrice: response.data.additionalUserPrice
        });
        setAdditionalUserConfirmOpen(true);
        return;
      }

      setSuccess(`Invitation sent successfully to ${inviteFormData.email}`);
      setInviteDialogOpen(false);
      fetchMembers();
      
      // If invite link is returned, show it
      if (response.data.inviteLink) {
        setSelectedInviteLink(response.data.inviteLink);
        setShowInviteLinkDialog(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send invitation';
      setError(errorMessage);

      // If upgrade required, show upgrade button
      if (error.response?.data?.requiresUpgrade) {
        // Could add upgrade button here
      }
    } finally {
      setInviting(false);
    }
  };

  const handleProceedWithPayment = async () => {
    if (!pendingMemberInvite || !paymentRequired) return;

    try {
      setInviting(true);
      setError('');
      
      // First create the member (pending status)
      const memberResponse = await api.post(`/api/business/${businessId}/members`, {
        email: pendingMemberInvite.email,
        role: pendingMemberInvite.role,
        permissions: pendingMemberInvite.permissions
      });

      const businessMemberId = memberResponse.data.member._id;

      // Create checkout session for adding seat
      const checkoutResponse = await api.post(`/api/business/${businessId}/subscriptions/add-seat-checkout`, {
        businessMemberId
      });

      // Redirect to Stripe checkout
      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create payment session');
      setInviting(false);
    }
  };

  const handleEditClick = (member) => {
    setEditingMember(member);
    setEditFormData({
      role: member.role || 'staff',
      permissions: member.permissions || []
    });
    setEditDialogOpen(true);
  };

  const handleEditRoleChange = (role) => {
    const defaultPerms = getDefaultPermissions(role);
    setEditFormData({
      ...editFormData,
      role,
      permissions: defaultPerms
    });
  };

  const handleEditPermissionToggle = (permission) => {
    const currentPerms = editFormData.permissions || [];
    const newPerms = currentPerms.includes(permission)
      ? currentPerms.filter(p => p !== permission)
      : [...currentPerms, permission];
    setEditFormData({
      ...editFormData,
      permissions: newPerms
    });
  };

  const handleSaveEdit = async () => {
    if (!editingMember) return;

    try {
      setLoading(true);
      setError('');
      await api.put(`/api/business/${businessId}/members/${editingMember._id}`, {
        role: editFormData.role,
        permissions: editFormData.permissions
      });

      setSuccess('Member updated successfully');
      setEditDialogOpen(false);
      setEditingMember(null);
      fetchMembers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update member');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (member) => {
    const memberName = member.user?.firstName 
      ? `${member.user.firstName} ${member.user.lastName || ''}`.trim()
      : member.invitedEmail || 'this member';
    
    // For pending invites, just confirm and remove
    if (member.status === 'pending') {
      const confirmMessage = `Are you sure you want to cancel the invitation for ${memberName}?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      try {
        setRemovingMember(true);
        setError('');
        await api.delete(`/api/business/${businessId}/members/${member._id}`);
        setSuccess('Invitation cancelled successfully');
        fetchMembers();
      } catch (error) {
        setError(error.response?.data?.message || 'Failed to cancel invitation');
      } finally {
        setRemovingMember(false);
      }
      return;
    }

    // For active members, show transfer dialog
    setMemberToRemove(member);
    setTransferToMemberId('');
    setTransferDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!memberToRemove) return;

    try {
      setRemovingMember(true);
      setError('');
      setTransferDialogOpen(false);
      
      // Remove subscription first if they have one (for seat-based billing, this will reduce seats)
      if (memberToRemove.status === 'active') {
        try {
          const _subResponse = await api.delete(`/api/business/${businessId}/subscriptions/members/${memberToRemove._id}`);
        } catch (subError) {}
      }

      // Remove the member with optional transfer
      const params = transferToMemberId ? { transferTo: transferToMemberId } : {};
      const response = await api.delete(`/api/business/${businessId}/members/${memberToRemove._id}`, {
        params
      });
      
      const memberName = memberToRemove.user?.firstName 
        ? `${memberToRemove.user.firstName} ${memberToRemove.user.lastName || ''}`.trim()
        : memberToRemove.invitedEmail || 'this member';
      
      let successMessage = `Member ${memberName} removed successfully.`;
      if (response.data.deletedSchedules > 0) {
        successMessage += ` ${response.data.deletedSchedules} schedule(s) ${transferToMemberId ? 'transferred' : 'deleted'}.`;
      }
      if (response.data.affectedBookings > 0) {
        successMessage += ` ${response.data.affectedBookings} booking(s) updated.`;
      }
      successMessage += ' Billing has been updated.';
      
      setSuccess(successMessage);
      setMemberToRemove(null);
      setTransferToMemberId('');
      fetchMembers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to remove member');
    } finally {
      setRemovingMember(false);
    }
  };

  const handleResendInvite = async (member) => {
    try {
      setInviting(true);
      setError('');
      const response = await api.post(`/api/business/${businessId}/members/${member._id}/resend-invite`);

      if (response.data.inviteSent) {
        setSuccess(`Invitation resent to ${member.invitedEmail || member.user?.email}`);
        if (response.data.inviteLink) {
          setSelectedInviteLink(response.data.inviteLink);
          setShowInviteLinkDialog(true);
        }
      } else {
        setError(response.data.error || 'Failed to resend invitation');
        if (response.data.inviteLink) {
          setSelectedInviteLink(response.data.inviteLink);
          setShowInviteLinkDialog(true);
        }
      }
      fetchMembers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to resend invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleCopyInviteLink = async (member) => {
    try {
      const response = await api.get(`/api/business/${businessId}/members/${member._id}/invite-link`);
      const inviteLink = response.data.inviteLink;
      
      await navigator.clipboard.writeText(inviteLink);
      setInviteLinkCopied(true);
      setSuccess('Invite link copied to clipboard!');
      setTimeout(() => {
        setInviteLinkCopied(false);
        setSuccess('');
      }, 3000);
    } catch (error) {
      setError('Failed to get invite link');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'error'; // Admin has same visual treatment as owner
      case 'manager':
        return 'warning';
      case 'staff':
        return 'default';
      default:
        return 'default';
    }
  };

  if (!hasPermission('manage_staff')) {
    return (
      <Alert severity="error">
        You don't have permission to manage team members.
      </Alert>
    );
  }

  if (loading && members.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography component="h2" className="pagename" sx={styles.text.pagename}>
            People
          </Typography>
          {(() => {
            const limitInfo = getUserLimitInfo();
            if (limitInfo.max !== null) {
              return (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {limitInfo.current} of {limitInfo.max} users ({limitInfo.planName} plan)
                </Typography>
              );
            } else if (limitInfo.planName === 'Enterprise') {
              return (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {limitInfo.current} users (Enterprise plan - unlimited)
                </Typography>
              );
            }
            return null;
          })()}
        </Box>
        {hasPermission('manage_staff') && (() => {
          const limitInfo = getUserLimitInfo();
          const planKey = limitInfo.planKey;
          
          // Only show invite button for Team or Enterprise plans
          if (planKey !== 'supplier_team' && planKey !== 'supplier_enterprise') {
            // Individual plan: Show upgrade prompt
            if (planKey === 'supplier_individual') {
              return (
                <Box>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Individual plan allows only 1 user. Upgrade to Team plan to invite team members.
                  </Alert>
                  <Button
                    variant="outlined"
                    onClick={() => window.location.href = '/supplier/billing'}
                  >
                    Upgrade to Team
                  </Button>
                </Box>
              );
            }
            
            // Free plan or other plans: Don't show invite button
            return null;
          }
          
          // Team or Enterprise plan: Show invite button
          // Check if limit reached
          if (limitInfo.max !== null && limitInfo.current >= limitInfo.max) {
            return (
              <Button
                variant="contained"
                startIcon={<UserPlus size={20} />}
                onClick={handleInviteClick}
                disabled
              >
                User Limit Reached
              </Button>
            );
          }
          
          return (
            <Button
              variant="contained"
              startIcon={<UserPlus size={20} />}
              onClick={handleInviteClick}
            >
              Invite Team Member
            </Button>
          );
        })()}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No team members found. Invite your first team member to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => {
                  const memberName = member.user?.firstName 
                    ? `${member.user.firstName} ${member.user.lastName || ''}`.trim()
                    : member.invitedEmail || 'Pending Invite';
                  const memberEmail = member.user?.email || member.invitedEmail;
                  const isPending = member.status === 'pending';

                  return (
                    <TableRow key={member._id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            src={getAvatarSrc(member.user?.profilePicture)}
                            sx={{ width: 32, height: 32 }}
                          >
                            {member.user?.firstName?.[0] || memberEmail?.[0]?.toUpperCase() || 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {memberName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {memberEmail}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={member.status || 'active'}
                          color={
                            member.status === 'pending' ? 'warning' :
                            member.status === 'inactive' ? 'default' :
                            member.status === 'suspended' ? 'error' :
                            'success'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={member.role || 'staff'}
                          color={getRoleColor(member.role)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {isPending ? (
                            <>
                              <Tooltip title="Resend Invite">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleResendInvite(member)}
                                    disabled={inviting}
                                  >
                                    <PaperPlane size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Copy Invite Link">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleCopyInviteLink(member)}
                                >
                                  <Copy size={18} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel Invite">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveMember(member)}
                                    disabled={removingMember}
                                  >
                                    <Trash size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              {/* Edit button - available for all members except owner (owner can't change their own role) */}
                              <Tooltip title="Edit Member">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEditClick(member)}
                                    disabled={member.role === 'owner'}
                                  >
                                    <Pencil size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {/* Remove button - available for all members except owner */}
                              <Tooltip title={member.role === 'owner' ? 'Cannot remove business owner' : 'Remove Member'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveMember(member)}
                                    disabled={removingMember || member.role === 'owner'}
                                  >
                                    <Trash size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteFormData.email}
              onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
              required
              sx={{ mb: 3 }}
              placeholder="team.member@example.com"
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteFormData.role}
                onChange={(e) => handleInviteRoleChange(e.target.value)}
                label="Role"
              >
                <MenuItem value="staff">Staff</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle2" gutterBottom>
              Permissions
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Default permissions for {inviteFormData.role} role. You can customise below.
            </Typography>
            
            <FormGroup>
              <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, display: 'flex', flexWrap: 'wrap' }}>
                {[
                  ...ALL_PERMISSION_KEYS
                ].map((permission) => (
                  <FormControlLabel
                    key={permission}
                    sx={{ width: { xs: '100%', sm: '50%' }, mr: 0 }}
                    control={
                      <Checkbox
                        checked={inviteFormData.permissions?.includes(permission) || false}
                        onChange={() => handlePermissionToggle(permission)}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">
                          {PERMISSION_DETAILS[permission]?.label || permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                        <Tooltip title={PERMISSION_DETAILS[permission]?.description || ''} placement="right">
                          <IconButton size="small" sx={{ p: 0.25 }}>
                            <Info size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  />
                ))}
              </Box>
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)} disabled={inviting}>
            Cancel
          </Button>
          <Button
            onClick={handleSendInvite}
            variant="contained"
            disabled={!inviteFormData.email || !inviteFormData.role || inviting}
            startIcon={inviting ? <CircularProgress size={16} /> : <UserPlus size={16} />}
          >
            {inviting ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Team Member</DialogTitle>
        <DialogContent>
          {editingMember && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Member
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {editingMember.user?.firstName 
                    ? `${editingMember.user.firstName} ${editingMember.user.lastName || ''}`.trim()
                    : editingMember.invitedEmail || 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {editingMember.user?.email || editingMember.invitedEmail}
                </Typography>
              </Box>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editFormData.role}
                  onChange={(e) => handleEditRoleChange(e.target.value)}
                  label="Role"
                  disabled={editingMember.role === 'owner'} // Owner role cannot be changed
                >
                  {editingMember.role === 'owner' ? (
                    <MenuItem value="owner" disabled>Owner (Reserved)</MenuItem>
                  ) : (
                    <>
                      <MenuItem value="staff">Staff</MenuItem>
                      <MenuItem value="manager">Manager</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>

              <Typography variant="body2" fontWeight={500} gutterBottom>
                Permissions
              </Typography>
              <FormGroup>
                <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, display: 'flex', flexWrap: 'wrap' }}>
                  {[
                    ...ALL_PERMISSION_KEYS
                  ].map((permission) => (
                    <FormControlLabel
                      key={permission}
                      sx={{ width: { xs: '100%', sm: '50%' }, mr: 0 }}
                      control={
                        <Checkbox
                          checked={editFormData.permissions?.includes(permission) || false}
                          onChange={() => handleEditPermissionToggle(permission)}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">
                            {PERMISSION_DETAILS[permission]?.label || permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                          <Tooltip title={PERMISSION_DETAILS[permission]?.description || ''} placement="right">
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              <Info size={14} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                    />
                  ))}
                </Box>
              </FormGroup>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Ownership Dialog */}
      <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Team Member</DialogTitle>
        <DialogContent>
          {memberToRemove && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={500} gutterBottom>
                  Removing {memberToRemove.user?.firstName 
                    ? `${memberToRemove.user.firstName} ${memberToRemove.user.lastName || ''}`.trim()
                    : memberToRemove.invitedEmail || 'this member'} will:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
                  <li>Remove their access to the platform</li>
                  <li>Delete all schedules connected to this user</li>
                  <li>Update billing (reduce seats if they have an active subscription)</li>
                  <li>Create an open seat if within contract term</li>
                </Typography>
              </Alert>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                To preserve schedules and bookings, you can transfer ownership to another team member:
              </Typography>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Transfer ownership to (optional)</InputLabel>
                <Select
                  value={transferToMemberId}
                  onChange={(e) => setTransferToMemberId(e.target.value)}
                  label="Transfer ownership to (optional)"
                >
                  <MenuItem value="">Delete schedules (no transfer)</MenuItem>
                  {members
                    .filter(m => 
                      m._id !== memberToRemove._id && 
                      m.status === 'active' &&
                      m.role !== 'owner'
                    )
                    .map((m) => (
                      <MenuItem key={m._id} value={m._id}>
                        {m.user?.firstName 
                          ? `${m.user.firstName} ${m.user.lastName || ''}`.trim()
                          : m.invitedEmail || 'Unknown'} ({m.role})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {transferToMemberId && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Schedules and booking assignments will be transferred to the selected member.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)} disabled={removingMember}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            variant="contained"
            color="error"
            disabled={removingMember}
          >
            {removingMember ? 'Removing...' : 'Remove Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Required Dialog */}
      <Dialog open={paymentCheckDialogOpen} onClose={() => setPaymentCheckDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Payment Required</DialogTitle>
        <DialogContent>
          {paymentRequired && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Adding a new team member requires a prorated payment for the remaining billing period.
                </Typography>
              </Alert>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Member Details:
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {pendingMemberInvite?.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Role: {pendingMemberInvite?.role}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Prorated Payment:
                </Typography>
                <Typography variant="h6" fontWeight={600} color="primary">
                  {formatGBPFromPounds(paymentRequired.proratedAmount)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Covers {paymentRequired.daysRemaining} days remaining in current billing period
                </Typography>
              </Box>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  After payment, the member will be added to your subscription and billing will be updated.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentCheckDialogOpen(false)} disabled={inviting}>
            Cancel
          </Button>
          <Button
            onClick={handleProceedWithPayment}
            variant="contained"
            disabled={inviting}
          >
            {inviting ? 'Processing...' : 'Proceed to Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Seat Confirmation Dialog */}
      <Dialog open={addSeatDialogOpen} onClose={() => setAddSeatDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Seat to Subscription</DialogTitle>
        <DialogContent>
          {pendingSeatAddition && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Changing this user's plan will increase your seats. The prorated amount will be charged immediately to your payment method.
                </Typography>
              </Alert>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Member:
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {pendingSeatAddition.member.user?.firstName || pendingSeatAddition.member.invitedEmail}
                  {pendingSeatAddition.member.user?.lastName && ` ${pendingSeatAddition.member.user.lastName}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Plan: {pendingSeatAddition.planKey?.charAt(0).toUpperCase() + pendingSeatAddition.planKey?.slice(1) || 'Current Plan'}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Seats:
                </Typography>
                <Typography variant="body1">
                  {pendingSeatAddition.currentSeats} → {pendingSeatAddition.newSeats} seats
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Billing cycle: {pendingSeatAddition.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Prorated Amount:
                </Typography>
                <Typography variant="h6" fontWeight={600} color="primary">
                  {formatGBPFromPounds(pendingSeatAddition.proratedAmount)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Covers {pendingSeatAddition.daysRemaining} {pendingSeatAddition.daysRemaining === 1 ? 'day' : 'days'} remaining in current billing period
                </Typography>
              </Box>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  This amount will be charged immediately to your payment method on file.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSeatDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!pendingSeatAddition) return;
              
              try {
                setLoading(true);
                setError('');
                
                const response = await api.post(`/api/business/${businessId}/subscriptions/add-seat-immediate`, {
                  businessMemberId: pendingSeatAddition.member._id,
                  membershipLevelKey: pendingSeatAddition.planKey
                });
                
                setSuccess(response.data.message || 'Seat added successfully');
                setAddSeatDialogOpen(false);
                setPendingSeatAddition(null);
                
                // Refresh all data
                await Promise.all([
                  fetchMembers(),
                  fetchSubscriptionDetails()
                ]);
              } catch (error) {
                setError(error.response?.data?.message || 'Failed to add seat');
              } finally {
                setLoading(false);
              }
            }}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Confirm & Add Seat'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Link Dialog */}
      <Dialog open={showInviteLinkDialog} onClose={() => setShowInviteLinkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Link</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {inviteLinkCopied 
              ? 'Invite link copied to clipboard!' 
              : 'Email may take a few minutes to arrive. You can copy and share this link directly:'}
          </Alert>
          <TextField
            fullWidth
            value={selectedInviteLink}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
            }}
            multiline
            rows={3}
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Copy size={18} />}
              onClick={async () => {
                await navigator.clipboard.writeText(selectedInviteLink);
                setInviteLinkCopied(true);
                setTimeout(() => setInviteLinkCopied(false), 3000);
              }}
            >
              {inviteLinkCopied ? 'Copied!' : 'Copy Link'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInviteLinkDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Additional User Confirmation Dialog */}
      <Dialog open={additionalUserConfirmOpen} onClose={() => !inviting && setAdditionalUserConfirmOpen(false)}>
        <DialogTitle>Add Additional Team Member</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You're adding your {pendingInvite?.willBeUserNumber || 4}th team member.
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your Team plan includes 3 base members. Adding this member will add <strong>£{formatGBPFromPounds(pendingInvite?.additionalUserPrice || 39.99)}{getPriceSuffix(pendingInvite?.additionalUserPrice || 39.99, 'month')}</strong> to your subscription.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This charge will be prorated for the remaining days in your billing period and added to your next invoice.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdditionalUserConfirmOpen(false)} disabled={inviting}>Cancel</Button>
          <Button onClick={handleConfirmAdditionalUser} variant="contained" disabled={inviting}>
            {inviting ? 'Sending...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default People;

